import { trigger } from '@angular/animations';
import { AsyncPipe, DatePipe, NgFor, NgIf, SlicePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { EmojiEvent } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { AssetDto, CommentDto, GalleryAccessLevel, GalleryDto, GuestUserDto, UserDto } from '@picflow/types';
import {
  Observable,
  Subject,
  Subscription,
  catchError,
  filter,
  firstValueFrom,
  forkJoin,
  map,
  of,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs';
import { maxHeightIf } from 'src/app/animations/max-height.if.animation';
import { ScreenSizes } from 'src/app/classes/screen-sizes.class';
import { GallerySettingsDialogComponent } from 'src/app/gallery/dialogs/gallery-settings-dialog/gallery-settings-dialog.component';
import { fixContentEditableStyles } from 'src/app/lib/fix-contenteditable-styles';
import { getAllowedAndEnabledFeedbackOptions } from 'src/app/lib/get-allowed-feedback-options';
import { isMetaKey } from 'src/app/lib/is-meta-key';
import { sleep } from 'src/app/lib/sleep';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { CleanUserGeneratedHtmlService } from 'src/app/services/clean-user-generated-html.service';
import { CommentService } from 'src/app/services/comment.service';
import { DialogService } from 'src/app/services/dialog.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { GuestAuthService } from 'src/app/services/guest-auth.service';
import { GuestService } from 'src/app/services/guest.service';
import { StateService } from 'src/app/services/state.service';
import { ToastService } from 'src/app/services/toast.service';
import { UpdateService } from 'src/app/services/update.service';
import { UserService } from 'src/app/services/user.service';
import { WatcherService, WatchingPerson } from 'src/app/services/watcher.service';
import { UserColorPipe } from 'src/app/shared-extra/pipes/user-color.pipe';
import { AvatarPipe } from '../../../shared-extra/pipes/avatar.pipe';
import { ButtonMenuItemComponent } from '../../../shared/components/button-menu-item/button-menu-item.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { WysiwygDirective } from '../../../wysiwyg/directives/wysiwyg.directive';
import { Comment } from '../../types/comment.type';
import { Shape } from '../../types/shapes';

export interface SimpleAnnotation {
  shapes: Shape[];
  commentId: string;
  frame?: number;
}

@Component({
  selector: 'app-asset-comments',
  templateUrl: './asset-comments.component.html',
  styleUrls: ['./asset-comments.component.scss'],
  animations: [trigger('annotationControls', maxHeightIf(48))],
  standalone: true,
  imports: [
    NgIf,
    SvgComponent,
    NgFor,
    TooltipDirective,
    RouterLink,
    WysiwygDirective,
    LoadingIndicatorComponent,
    ContextMenuComponent,
    ButtonDirective,
    ButtonMenuItemComponent,
    ResizeDirective,
    AsyncPipe,
    SlicePipe,
    DatePipe,
    TranslatePipe,
    AvatarPipe,
    UserColorPipe,
  ],
})
export class AssetCommentsComponent implements OnInit, OnDestroy, OnChanges {
  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    const metaKey = isMetaKey(event);
    if (event.target['getAttribute']?.('name') === 'comment') {
      if (event.key === 'Enter' && metaKey) this.submitMessage();
    }
    if (event.target['getAttribute']?.('name') === 'edit-comment') {
      if (event.key === 'Enter' && metaKey) this.saveEditing();
    }
  }
  @Input() asset: AssetDto;
  @Input() gallery: GalleryDto;
  @Input() accessLevel: GalleryAccessLevel;
  // @Output() onAnnotationHistoryChange: EventEmitter<'undo' | 'redo'> = new EventEmitter();
  @Output() onSendComment: EventEmitter<string> = new EventEmitter();
  // @Output() onAnnotation: EventEmitter<boolean> = new EventEmitter();
  // @Output() onSwitchTool: EventEmitter<AnnotationTool> = new EventEmitter();
  // @Output() onSwitchColor: EventEmitter<AnnotationColor> = new EventEmitter();
  @Output() onViewAnnotation: EventEmitter<SimpleAnnotation> = new EventEmitter();
  @Output() onClose = new EventEmitter();
  @Input() initialActiveAnnotation: SimpleAnnotation;
  @ViewChild('assetActivity') assetActivity: ElementRef<HTMLDivElement>;
  @ViewChild('emojiselectortemp', { read: ViewContainerRef })
  private emojiselectorviewcontainerref: ViewContainerRef;
  @ViewChild('commentField') public commentFieldRef: ElementRef;
  private emojiComponentSubscription: Subscription;
  public message = '';
  allowedAndEnabledFeedbackOptions: string[];
  loadingWatchers: boolean;
  annotationMode: boolean;
  watchers: WatchingPerson[];
  sending = false;
  user: UserDto;
  guest: GuestUserDto;
  myId: string;
  editing: Comment;
  activeAnnotation: SimpleAnnotation;
  messageFocused = false;
  updateComments$ = new Subject<void>();

  guestAuthSub = this.guestAuthService.onAuthChange.subscribe(auth => {
    if (!auth) return;
    this.loadWatchers();
    this.updateComments$.next();
  });

  constructor(
    private vcref: ViewContainerRef,
    private userService: UserService,
    private guestService: GuestService,
    private guestAuthService: GuestAuthService,
    private commentService: CommentService,
    private watcherService: WatcherService,
    private analytics: AnalyticsService,
    private route: ActivatedRoute,
    public state: StateService,
    public featureFlags: FeatureFlagsService,
    private updateService: UpdateService,
    private dialog: DialogService,
    private cleanUserGeneratedHtmlService: CleanUserGeneratedHtmlService,
    private toasts: ToastService
  ) {}

  async ngOnInit() {
    this.allowedAndEnabledFeedbackOptions = getAllowedAndEnabledFeedbackOptions(
      this.gallery,
      this.state.plan,
      this.accessLevel
    );
    this.activeAnnotation = this.initialActiveAnnotation;
    if (ScreenSizes.screenSize() === 'desktop' && this.allowedAndEnabledFeedbackOptions.includes('comment')) {
      this.loadEmojiSelector();
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.asset) {
      this.loadWatchers();
      this.updateComments$.next();
      if (changes.asset.previousValue) {
        this.updateService.unsubscribeAsset(changes.asset.previousValue.id);
      }
      this.updateService.subscribeAsset(this.asset.id);
      if (this.activeAnnotation) this.unViewAnnotation();
    }
  }

  openGallerySettings() {
    this.dialog.open(GallerySettingsDialogComponent, { gallery: this.gallery, defaultMode: 'review' });
  }

  async loadEmojiSelector() {
    this.vcref.clear();
    const { EmojiSelectorComponent } = await import('../lazy/emoji-selector/emoji-selector.component');
    const x = this.emojiselectorviewcontainerref.createComponent(EmojiSelectorComponent);
    this.emojiComponentSubscription = x.instance.onSelect.subscribe(event => this.addEmoji(event));
  }

  initAuth() {
    return forkJoin([this.userService.getUser(), this.guestService.getGuest()]).pipe(
      tap(([user, guest]) => {
        this.user = user;
        this.guest = guest;
        this.setMyId();
      })
    );
  }

  private setMyId() {
    if (!this.user && !this.guest) return;
    const id = this.user ? this.user.id : this.guest['id'];
    this.myId = id;
  }

  viewAnnotation(comment: Comment) {
    const shapes = comment.annotation;
    if (this.activeAnnotation?.commentId === comment.id) return this.unViewAnnotation();
    else
      this.activeAnnotation = {
        commentId: comment.id,
        shapes,
        frame: comment.position?.frame?.[0] || comment.position?.frame,
      };
    this.onViewAnnotation.emit(this.activeAnnotation);
    if (['mobile', 'tablet'].includes(ScreenSizes.screenSize())) this.onClose.emit();
  }

  unViewAnnotation() {
    if (!this.activeAnnotation) return;
    this.activeAnnotation = undefined;
    this.onViewAnnotation.emit(undefined);
  }

  comments$: Observable<{
    days: {
      date: string;
      comments: Comment[];
      timestamp: number;
    }[];
    comments: CommentDto[];
  }>;

  onUpdateComments = this.updateComments$.subscribe(() => {
    if (!this.asset) return;
    this.comments$ = this.initAuth().pipe(
      switchMap(() => {
        return this.commentService.getComments(this.asset.id, this.gallery.id);
      }),
      map(comments => {
        this.sending = false;
        return {
          days: this.processComments(comments),
          comments,
        };
      }),
      tap(() => {
        this.scrollCommentsToBottom();
      })
    );
  });

  urlParamWatcher = this.route.queryParams.subscribe(params => {
    const id = params.annotation;
    if (!id) return;
    return this.comments$.subscribe(({ comments, days }) => {
      const comment = comments.find(comment => comment.annotation?.id === id);
      if (!comment) return console.error('Invalid annotation ID');
      const day = days.find(d => d.comments.find(c => c.id === comment['id']));
      const _comment = day.comments.find(c => c.id === comment['id']);
      this.viewAnnotation(_comment);
    });
  });

  public async loadWatchers() {
    await firstValueFrom(this.initAuth());
    this.loadingWatchers = true;
    this.watchers = await this.watcherService.getAssetWatchers(this.asset.id, this.gallery.id);
    // Remove ourselves from the list
    this.watchers = this.watchers.filter(w => {
      if (w?.id) return w.id !== this.myId;
      return (w as unknown as string) !== this.myId; // ??
    });
    this.loadingWatchers = false;
  }

  private async scrollCommentsToBottom() {
    await sleep(0);
    this.assetActivity.nativeElement.scrollTop = this.assetActivity.nativeElement.scrollHeight;
  }

  private processComments(comments: CommentDto[]) {
    const days: { date: string; comments: Comment[]; timestamp: number }[] = [];
    comments.sort((a, b) => (a['created_at'] > b['created_at'] ? 1 : -1));
    comments.forEach(comment => {
      if (!comment.guest && !comment.user) return;
      const date = new Date(comment['created_at'] * 1000);
      const ye = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
      const mo = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
      const da = new Intl.DateTimeFormat('en', { day: 'numeric' }).format(date);
      const string = `${mo} ${da}, ${ye}`;
      let day = days.find(d => d.date === string);
      if (!day) {
        days.push({
          date: string,
          timestamp: date.getTime(),
          comments: [],
        });
        day = days[days.length - 1];
      }
      const name = comment.user
        ? comment.user.last_name
          ? `${comment.user.first_name} ${comment.user.last_name}`
          : comment.user.first_name
        : comment.guest.name;

      const authorId = comment.user ? comment.user.id : comment.guest.id;
      const authorType: 'user' | 'guest' = comment.user ? 'user' : 'guest';

      let type: 'normal' | 'meta' = 'meta';
      let subType: string;

      let text = comment.text;
      if (comment.text || comment.annotation) {
        type = 'normal';
        text = this.cleanUserGeneratedHtmlService.clean(text);
      } else {
        if (comment.status) {
          subType = 'status';
          if (comment.status.status === 'approved') text = `approved this asset`;
          if (comment.status.status === 'rejected') text = `rejected this asset`;
          if (comment.status.status === 'no_status') text = `removed status`;
        } else if (comment.color_label) {
          subType = 'color_label';
          if (!comment.color_label.color_label) text = 'removed color label';
          else text = `added ${comment.color_label.color_label} label`;
        } else if (comment.asset_version_event) {
          if (comment['created_at'] - this.asset.created_at > 1) {
            text = 'uploaded a new asset version';
          } else {
            // text = 'uploaded this asset';
            // we don't want our own first upload to be displayed
            return;
          }
        }

        if (!text && !comment.annotation) {
          console.warn('No text for this comment type', comment);
          return;
        }
      }

      const _comment = {
        type,
        subType,
        authorType,
        text,
        origText: comment.text,
        id: comment['id'],
        name,
        authorId,
        author: comment.user || comment.guest,
        // color,
        createdDate: comment['created_at'] * 1000,
        likes: comment.likes,
        annotation: <Shape[]>comment.annotation?.options,
        position: comment.position,
      };

      const previous = day.comments[day.comments.length - 1];

      // If the previous comment and this one have the same type and are both from the same user and they are less than x minutes appart then we only want the newer one
      if (previous?.subType && _comment.subType === previous.subType && _comment.authorId === previous.authorId) {
        const timeDiff = _comment.createdDate - previous.createdDate;
        if (timeDiff < 1000 * 60 * 10) {
          day.comments.pop();
        }
      }

      day.comments.push(_comment);
    });
    const notEmptyDays = days.filter(d => d.comments.length);
    notEmptyDays.sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1));

    notEmptyDays.forEach(day => {
      day.comments.forEach(comment => {
        comment.isFirstOfGroup = this.isFirstOfGroup(comment, day.comments);
        comment.isLastOfGroup = this.isLastOfGroup(comment, day.comments);
      });
    });
    return notEmptyDays;
  }

  addEmoji(emoji: EmojiEvent) {
    this.message += emoji.emoji.native;
  }

  async submitMessage() {
    this.message = fixContentEditableStyles(this.message).trim();
    if (this.sending) return;
    if (!this.message) return;
    this.onSendComment.emit(this.message);

    if (this.myId) {
      const comment = <CommentDto>{
        text: this.message,
        user: this.user
          ? {
              id: this.user.id,
              first_name: this.user.first_name,
              last_name: this.user.last_name,
              avatar_url: this.user.avatar_url,
              last_activity_at: this.user.last_activity_at,
            }
          : null,
        guest: this.guest
          ? {
              id: this.guest['id'],
              name: this.guest.name,
              anonymous_id: this.guest.anonymous_id,
              last_activity_at: this.guest.last_activity_at,
            }
          : null,
      };

      comment['created_at'] = Math.floor(Date.now() / 1000);
      this.scrollCommentsToBottom();
      this.sending = true;
    }

    this.message = '';
  }

  editComment(comment: Comment) {
    this.editing = comment;
  }

  cancelEditing() {
    this.editing.text = this.editing.origText;
    this.editing = undefined;
  }

  saveEditing() {
    if (!this.editing.text) return;
    const text = fixContentEditableStyles(this.editing.text).trim();
    this.commentService.updateComment(this.asset.id, this.editing.id, { text }, this.gallery.id).subscribe();
    this.editing = undefined;
    this.analytics.track('asset_comment_edited');
  }

  async deleteComment(comment: Comment) {
    await firstValueFrom(this.commentService.deleteComment(this.asset.id, comment.id, this.gallery.id));
    this.analytics.track('asset_comment_deleted');
  }

  private isLastOfGroup(comment: Comment, comments: Comment[]) {
    const index = comments.indexOf(comment);
    const next = comments[index + 1];

    if (!next) return true;
    if (comment.type !== next.type) return true;
    if (comment.type === 'meta') return false; // Meta comments can always be one group even if the time between was a lot and the author is different
    if (comment.authorId !== next.authorId) return true;
    return next.createdDate - comment.createdDate > 60 * 1000 * 1; // One minute apart
  }

  private isFirstOfGroup(comment: Comment, comments: Comment[]) {
    const index = comments.indexOf(comment);
    const previous = comments[index - 1];
    if (!previous) return true;
    return this.isLastOfGroup(previous, comments);
  }

  testWebsocketMessages(count = 0) {
    if (!count) this.toasts.add('testing websockets', 'info');
    this.onSendComment.emit(`test ${count}`);

    this.updateService.onCommentUpdate
      .pipe(
        filter(event => {
          if (!event) return;
          console.log(event);
          if (event.name !== 'comment.created') return;
          console.log(event);
          if (event.comment.text !== `test ${count}`) return;
          return true;
        }),
        take(1),
        timeout(1000 * 2),
        tap(() => {
          if (count > 10) return this.toasts.add('websocket test done', 'success');
          console.log('got websocket message', count);
          this.testWebsocketMessages(count + 1);
        }),
        catchError(() => {
          this.toasts.add('websocket test failed', 'error');
          return of(null);
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.guestAuthSub?.unsubscribe();
    this.emojiComponentSubscription?.unsubscribe();
    this.updateService.unsubscribeAsset(this.asset.id);
  }
}
